import { Dispatch } from 'redux';
import {
  downloadTaxBill,
  exportTaxBillRecord,
  getTaxBillRecord,
} from './TaxTraxTaxBillRecord.services';
import {
  httpError,
  json,
  showWarnNotification,
  blob,
  showSuccessNotification,
  ExportFormatNames,
} from 'common';
import {
  TAXTRAX_TAX_BILL_RECORD_ADD,
  TAXTRAX_TAX_BILL_RECORD_CLEAR,
  TAXTRAX_TAX_BILL_RECORD_LOADING,
} from './TaxTraxTaxBillRecord.types';
import { TaxBill, TaxBillRecord } from '../models';
import { printFile, saveFile } from 'common/helpers';
import {
  Property,
  PropertyIdentifier,
  PropertyInformation,
} from 'common/models';

export function taxTraxTaxBillRecordAdd(
  taxBill: TaxBill,
  taxBillRecord: TaxBillRecord,
) {
  return {
    type: TAXTRAX_TAX_BILL_RECORD_ADD,
    payload: { taxBill, taxBillRecord },
  };
}

export function taxTraxTaxBillRecordClear() {
  return {
    type: TAXTRAX_TAX_BILL_RECORD_CLEAR,
  };
}

export function taxTraxTaxBillRecordLoading(loading: boolean) {
  return {
    type: TAXTRAX_TAX_BILL_RECORD_LOADING,
    payload: loading,
  };
}

export function fetchTaxTraxTaxBillRecordAsync(
  taxBill: TaxBill,
  property: Property,
  bearerToken: string,
) {
  return (dispatch: Dispatch) => {
    const requestBody = {
      apn: property.apn,
      taxType: taxBill.taxType,
      year: taxBill.year,
      periodId: taxBill.periodId,
      hob: property.hob,
    };
    dispatch(taxTraxTaxBillRecordLoading(true));
    getTaxBillRecord(requestBody, bearerToken)
      .then(httpError(dispatch))
      .then(json)
      .then((response) => {
        if (response.errorMessage) throw Error(response.errorMessage);
        return response;
      })
      .then((taxBillRecord: TaxBillRecord) => {
        dispatch(taxTraxTaxBillRecordLoading(false));
        return dispatch(taxTraxTaxBillRecordAdd(taxBill, taxBillRecord));
      })
      .catch((error) => {
        dispatch(taxTraxTaxBillRecordLoading(false));
        showWarnNotification(`Fetch tax bill record`, error.message);
      });
  };
}

export function exportTaxBillRecordAsync(
  taxBillRecord: TaxBillRecord,
  taxBill: TaxBill,
  property: Property,
  propertyInformation: PropertyInformation,
  propertyIdentifier: PropertyIdentifier,
  format: string,
  action: 'print' | 'save',
  token: string,
) {
  return (dispatch: Dispatch) => {
    const requestBody = {
      taxBillRecord: taxBillRecord,
      taxBill: taxBill,
      property: property,
      propertyInformation: propertyInformation,
      propertyIdentifier: propertyIdentifier,
      searchId: taxBillRecord.searchId,
    };
    dispatch(taxTraxTaxBillRecordLoading(true));
    exportTaxBillRecord(requestBody, token, format)
      .then(httpError(dispatch))
      .then(blob)
      .then((blob) => {
        dispatch(taxTraxTaxBillRecordLoading(false));
        if (action === 'print') {
          printFile(blob);
        }
        if (action === 'save') {
          saveFile(blob, `Report.${format}`);
          showSuccessNotification(`Fetch file`, `File was exported`);
        }
      })
      .catch((error) => {
        dispatch(taxTraxTaxBillRecordLoading(false));
        showWarnNotification(`Fetch tax bill record file`, error.message);
      });
  };
}

export function downloadTaxBillHtmlAsync(
  taxBill: TaxBill,
  hob: string,
  token: string,
) {
  return (dispatch: Dispatch) => {
    const requestBody = {
      apn: taxBill.apn,
      taxType: taxBill.taxType,
      year: taxBill.year,
      periodId: taxBill.periodId,
      fileFormat: ExportFormatNames.HTML,
      fileName: getTaxBillFileName(taxBill, ExportFormatNames.HTML),
      hob: hob,
    };
    dispatch(taxTraxTaxBillRecordLoading(true));
    downloadTaxBill(requestBody, token)
      .then(httpError(dispatch))
      .then(blob)
      .then((blob) => {
        dispatch(taxTraxTaxBillRecordLoading(false));
        saveFile(blob, requestBody.fileName);
        showSuccessNotification(
          `Fetch bill preview file`,
          `File was downloaded`,
        );
      })
      .catch((error) => {
        dispatch(taxTraxTaxBillRecordLoading(false));
        showWarnNotification(`Fetch tax bill preview file`, error.message);
      });
  };
}

export function downloadTaxBillPdfAsync(
  taxBill: TaxBill,
  hob: string,
  token: string,
) {
  return (dispatch: Dispatch) => {
    const requestBody = {
      apn: taxBill.apn,
      taxType: taxBill.taxType,
      year: taxBill.year,
      periodId: taxBill.periodId,
      fileFormat: ExportFormatNames.PDF,
      fileName: getTaxBillFileName(taxBill, ExportFormatNames.PDF),
      hob: hob,
    };
    dispatch(taxTraxTaxBillRecordLoading(true));
    downloadTaxBill(requestBody, token)
      .then(httpError(dispatch))
      .then(blob)
      .then((blob) => {
        dispatch(taxTraxTaxBillRecordLoading(false));
        saveFile(blob, requestBody.fileName);
        showSuccessNotification(`Fetch bill file`, `File was downloaded`);
      })
      .catch((error) => {
        dispatch(taxTraxTaxBillRecordLoading(false));
        showWarnNotification(`Fetch tax bill file`, error.message);
      });
  };
}

export function getTaxBillFileName(
  taxBill: TaxBill,
  fileFormat: ExportFormatNames,
) {
  const nameSuffix = fileFormat === ExportFormatNames.HTML ? '_Preview' : '';
  return `${taxBill.apn}_${taxBill.year}_${taxBill.taxTypeName}${nameSuffix}.${fileFormat}`;
}
